import * as React from 'react';
import { Trans } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, Switch, Route } from 'react-router-dom';

// Utils
import {
  isNil,
  isNilOrEmpty,
  verifyProperty,
} from '@src/shared/src/util/general';
import {
  navigate,
  isCurrentPath,
} from '@toolkit/util/app';
import { t } from '@toolkit/util/i18n';
import { AppContextProp, withAppContext } from '@toolkit/util/AppContext';
// Constants
import { ENVIRONMENT, STATUS } from '@src/shared/src/const/app';
import { CHECKOUT_STEPS, ROUTES } from '@toolkit/const/app';
import { API_URL } from '@src/shared/src/const/api';

// Actions
import { selectors } from '@src/shared/src';
// Models
import {
  BasketItemModel,
  BasketModel,
  BookingItemModel,
  BookingModel,
  PolicyViolationModel,
  SearchModel,
  TravelBookingModel,
  RentalModel,
  RedeemingAllowanceModel,
  PassengerModel,
} from '@src/shared/src/models';
// Interfaces
import { ConnectedRedux, IRootState } from '@src/store';
import { StepParam, SearchIdParam } from '@src/routes/IMatchParams';
// Components
import CheckoutSideBarConn from './CheckoutSideBarConn';
import CheckoutReserveConn from './CheckoutReserveConn';
import CheckoutPrepareConn from './CheckoutPrepareConn';
import CheckoutPaymentConn from './CheckoutPaymentConn';
import CheckoutConfirmConn from './CheckoutConfirmConn';
import { CheckoutTimer, ShowLastErrorStatus, CheckoutOverlayMessages } from '@pod/checkout/components';
import { getFailedMessageSource, getFailedMessageStatus } from './failedMessage.util';
import {
  InformationOverlay,
  LoaderOverlay,
  BENotificationBar,
  LoaderProgress,
} from '@toolkit/ui';
import CheckoutMobileNavbar from '../components/CheckoutMobileNavbar';
import CheckoutBanners from '../components/CheckoutBanners';
import { ErrorIcon } from '@toolkit/ui/icons/ErrorIcon';
import { RestartIcon } from '@toolkit/ui/icons/RestartIcon';
// Styles

type Props = ConnectedRedux<IRootState> & RouteComponentProps<SearchIdParam & StepParam> & AppContextProp & {
  basket:BasketModel;
  basketItems:BasketItemModel[];
  search:SearchModel;
  passengers:PassengerModel[];
  totalCost:number;
  currentBooking:BookingModel;
  travelBooking:TravelBookingModel;
  currentBookingItems:BookingItemModel[];
  hasConfirmFailedItems:boolean;
  hasVoidedItems:boolean;
  hasConfirmPendingItems:boolean;
  hasHardFailedItems:boolean;
  policyViolations:PolicyViolationModel[];
  preparedAt:Date;
  seatMapSelectorUrl:string;
  bookingRental:RentalModel;
  redeemingAllowance: RedeemingAllowanceModel;
};

type State = {
  env:ENVIRONMENT;
  showTimerExpiredOverlay:boolean;
  showDownloadICal:boolean;
  hotelRemarks:string;
};

class CheckoutConn extends React.Component<Props, State> {

  constructor(props:Props) {
    super(props);
    this.state = {
      env: ENVIRONMENT.CHECKOUT,
      showTimerExpiredOverlay: false,
      showDownloadICal: false,
      hotelRemarks:undefined,
    }
  }

  private isFailedOverlayVisible = (basketStatus:STATUS) => {
    switch (basketStatus) {
      case STATUS.FAILED:
      case STATUS.PREPARE_FAILED:
        return true;
      default:
        return false;
    }
  }

  private getEnvironemntByBasketStatus = (basketStatus:STATUS) => {
    switch (basketStatus) {
      case STATUS.FRESH:
      case STATUS.FAILED:
      case STATUS.PREPARE_FAILED:
      case STATUS.PREPARE_FINISHED:
      case STATUS.IN_PROGRESS:
        return ENVIRONMENT.CHECKOUT;
      case STATUS.CONFIRM_FAILED:
      case STATUS.CONFIRM_PENDING:
      case STATUS.CONFIRM_FINISHED:
      case STATUS.FINISHED:
        return ENVIRONMENT.CONFIRMATION;
      default:
        return ENVIRONMENT.CHECKOUT;
    }
  }

  private failedMessage = (): React.ReactNode => {
    const failedMessageSource = getFailedMessageSource(this.props.currentBookingItems, this.props.basketItems);

    if (failedMessageSource) {
      return <span dangerouslySetInnerHTML={{ __html: failedMessageSource }} />;
    }

    const failedStatus = getFailedMessageStatus(this.props.currentBookingItems);
    const i18nKey = failedStatus
      ? `checkoutConn.lastErrorStatus.text.${failedStatus}`
      : 'CheckoutConn.failed.info.message';

    return (
      <Trans
        i18nKey={i18nKey}
        defaults={t('CheckoutConn.failed.info.message')}
        components={[<b />]}
      />
    );
  }

  private informationOverlayTitle = () => {
    return t('informationOverlay.title.basketFailed');
  }

  public render() {
    if (isNil(this.props.basket) || isNilOrEmpty(this.props.passengers)) {
      return (
        <LoaderOverlay
          showSpinner={true}
          title={t('global.processingTrip')}/>
      );
    }

    const basketEditable:boolean = verifyProperty(null, this.props.basket, ['editable']);

    const { match, appContext, search, currentBooking } = this.props;

    const basketId = this.props.search.basketId;
    const searchId = this.props.search.id;
    const basketStatus = this.props.basket.status as STATUS;
    const totalPrice = verifyProperty(null, this.props.currentBooking, ['totalPrice']);
    const bookingId = verifyProperty(null, this.props.currentBooking, ['id']);
    const possibleCheaperFlight = verifyProperty(null, currentBooking, ['askSupportIfCheaperTicketPossible']);
    const requiresAllowance = verifyProperty(null, currentBooking, ['requiresAllowance']);
    const exceedsAllowance = verifyProperty(null, currentBooking, ['exceedsAllowance']);
    const redeemingAllowance = verifyProperty(null, currentBooking, ['redeemingAllowance']);

    return (
      <React.Fragment>
        <ShowLastErrorStatus />
        <div className="main-container">
          <BENotificationBar currentPage="Booking" />
          {basketStatus === STATUS.IN_PROGRESS && <LoaderProgress finished={false}/>}
            <div className="row">
              {appContext.isMediaBPTabletUp ?
                <CheckoutSideBarConn
                  env={this.getEnvironemntByBasketStatus(basketStatus)}
                  isBasketEditable={basketEditable}
                  exceedsAllowance={exceedsAllowance}
                  bookingId={bookingId}
                  onTimerExpired={() => this.setState({ showTimerExpiredOverlay: true })}/>
                  :
                <CheckoutMobileNavbar
                  basketStatus={basketStatus}
                  search={search}
                  isBasketEditable={basketEditable}
                />
              }
            <section className="content content-checkout">
              <CheckoutBanners
                bookingRental={this.props.bookingRental}
                currentBookingItems={this.props.currentBookingItems}
                hasConfirmFailedItems={this.props.hasConfirmFailedItems}
                hasHardFailedItems={this.props.hasHardFailedItems}
                hasVoidedItems={this.props.hasVoidedItems}
                hasConfirmPendingItems={this.props.hasConfirmPendingItems}
                passengers={this.props.passengers}
                policyViolations={this.props.policyViolations}
                redeemingAllowance={redeemingAllowance}
                exceedsAllowance={exceedsAllowance}
                requiresAllowance={requiresAllowance}
                failedMessage={this.failedMessage()}
              />
              {!appContext.isMediaBPTabletUp && basketStatus === STATUS.PREPARE_FINISHED ?
                <CheckoutTimer
                onTimerExpired={() => this.setState({ showTimerExpiredOverlay: true })}
                bookingCreatedAt={this.props.preparedAt}/> : ''
              }
              <Switch>
                <Route
                  path={`${match.path}${CHECKOUT_STEPS.RESERVE}/:searchId`}
                  render={
                    (props) =>
                      <CheckoutReserveConn
                        env={ENVIRONMENT.CHECKOUT}
                        basketStatus={basketStatus}
                        basketId={basketId}
                        searchId={searchId}/>
                  }/>
                <Route
                  path={`${match.path}${CHECKOUT_STEPS.PREPARE}/:searchId`}
                  render={
                    (props) =>
                      <CheckoutPrepareConn
                        env={ENVIRONMENT.CHECKOUT}
                        totalPrice={totalPrice}
                        bookingItems={this.props.currentBookingItems}
                        basketItems={this.props.basketItems}
                        basketStatus={basketStatus}
                        onChangeHotelRemarks={(hotelRemarks) => this.setState({ hotelRemarks })}
                        seatMapSelectorUrl={this.props.seatMapSelectorUrl}
                        bookingId={bookingId}
                        possibleCheaperFlight={possibleCheaperFlight}
                        requiresAllowance={requiresAllowance}
                        exceedsAllowance={exceedsAllowance}
                        redeemingAllowance={redeemingAllowance && !exceedsAllowance}
                        search={search}/>
                  }/>
                <Route
                  path={`${match.path}${CHECKOUT_STEPS.PAYMENT}/:searchId`}
                  render={
                    (props) =>
                      <CheckoutPaymentConn
                        basketId={basketId}
                        totalPrice={totalPrice}
                        basketStatus={basketStatus}
                        policyViolations={this.props.policyViolations}
                        hotelRemarks={this.state.hotelRemarks}
                        searchId={searchId}/>
                  }/>
                <Route
                  path={`${match.path}${CHECKOUT_STEPS.CONFIRM}/:searchId`}
                  render={
                    (props) =>
                      <CheckoutConfirmConn
                        env={ENVIRONMENT.CONFIRMATION}
                        totalPrice={totalPrice}
                        bookingItems={this.props.currentBookingItems}
                        basketStatus={basketStatus}
                        />
                  }/>
              </Switch>
            </section>
          </div>
          <div>
            {/*TODO: merge all information overlay stuff to here */}
            <CheckoutOverlayMessages />
            <InformationOverlay
              key={`info-overlay-failed-${this.isFailedOverlayVisible(basketStatus)}`}
              isOpen={this.isFailedOverlayVisible(basketStatus)}
              icon={<ErrorIcon />}
              title={t('informationOverlay.title.basketFailed')}
              body={this.failedMessage()}
              showButton={true}
              buttonText={t('informationOverlay.button.basketFailed')}
              onButtonClicked={() => navigate(ROUTES.HOME)}/>
            <InformationOverlay
              key={`info-overlay-confirm-failed`}
              isOpen={
                (this.props.hasConfirmFailedItems ||
                  this.props.hasVoidedItems ||
                  this.props.hasConfirmPendingItems) &&
                !this.props.hasHardFailedItems
              }
              icon={<ErrorIcon />}
              title={this.informationOverlayTitle()}
              body={this.failedMessage()}
              showButton={true}
              buttonText={t('informationOverlay.button.continue')}/>
            <InformationOverlay
              key="info-overlay-confirm-hard-failed"
              isOpen={this.props.hasHardFailedItems}
              icon={<RestartIcon />}
              body={t('CheckoutConn.hardFailed.info.message')}
              showButton={true}
              buttonText={t('informationOverlay.button.proceedTimerExpired')}
              onButtonClicked={() => navigate(ROUTES.HOME)}/>
            <InformationOverlay
              key={`info-overlay-timer-expired-${this.state.showTimerExpiredOverlay}`}
              isOpen={this.state.showTimerExpiredOverlay && !(isCurrentPath(ROUTES.BOOKING.CONFIRM))}
              icon={<RestartIcon />}
              title={t('informationOverlay.title.timerExpired')}
              body={
                redeemingAllowance
                ? <form
                    method="post"
                    action={`${API_URL.DEFAULT}/org_admin/allowances/${redeemingAllowance.id}/redeem`}>
                    <button className="tcp-loader-overlay-button button" type="submit">
                      {t('exceedsAllowanceWarning.exceedsAllowance.link.redeem')}
                    </button>
                  </form>
                : <Trans i18nKey="informationOverlay.body.timerExpired">
                    <br/>
                  </Trans>}
              showButton={redeemingAllowance ? false : true}
              buttonText={t('informationOverlay.button.proceedTimerExpired')}
              onButtonClicked={() => navigate(ROUTES.HOME)}/>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapState = (state:IRootState) => ({
  basket: state.checkout.basket,
  basketItems: selectors.checkout.basketItems(state.checkout),
  search: selectors.search.search(state.search),
  passengers: selectors.search.searchPassengers(state.search),
  totalCost: selectors.checkout.totalCost(state.checkout),
  currentBooking: state.checkout.currentBooking,
  travelBooking: state.checkout.travelBooking,
  currentBookingItems: state.checkout.currentBookingItems,
  hasConfirmFailedItems: selectors.checkout.hasConfirmFailedItems(state.checkout),
  hasVoidedItems: selectors.checkout.hasVoidedItems(state.checkout),
  hasConfirmPendingItems: selectors.checkout.hasConfirmPendingItems(state.checkout),
  hasHardFailedItems: selectors.checkout.hasHardFailedItems(state.checkout),
  policyViolations: selectors.checkout.policyViolations(state.checkout),
  preparedAt: selectors.checkout.preparedAt(state.checkout),
  seatMapSelectorUrl: state.checkout.seatMapSelectorUrl,
  bookingRental: selectors.checkout.rental(state.checkout),
  redeemingAllowance: state.checkout.redeemingAllowance,
});
export default connect(mapState)(withAppContext(CheckoutConn));
